import {
  Avatar,
  Box,
  ButtonBase,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  tableCellClasses,
} from "@mui/material";
import { Fragment, useCallback, useContext } from "react";
import { formatName } from "../../features/Coaches/coach.utils";
import { I18nContext } from "../../features/I18n/I18nProvider";
import { QueryRenderer } from "../../features/QM/QueryRenderer";
import { gray200 } from "../../theme";
import { Icon } from "../Icon";
import { P } from "../Typography";
import { prop } from "ramda";

export const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme, variant = "emphasized" }) => ({
  borderRight: "1px solid #EAECF0",
  paddingTop: "12px",
  paddingBottom: "12px",
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: theme.palette.common.black,
    // color: theme.palette.common.white,
    color: "#667085",
    fontSize: 14,
    fontWeight: 500,
  },
  [`&.${tableCellClasses.body}`]:
    variant === "emphasized"
      ? {
          fontSize: 16,
          fontWeight: 600,
        }
      : variant === "actionable"
        ? {
            width: "100%",
            [`&:hover`]: {
              backgroundColor: "#f5f5f5",
            },
          }
        : {},
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    // backgroundColor: theme.palette.action.hover,
  },
  backgroundColor: "white",
  // hide last border
  "&:last-child td, &:last-child th": {
    // border: 0,
  },
}));

export const TLChipsCell = ({ children, chips = [], ...props }) => {
  return (
    <StyledTableCell {...props}>
      <Stack direction="row" spacing={1}>
        {chips?.map?.(({ label, bgcolor }) => (
          <Chip
            key={label}
            label={label}
            sx={{ bgcolor, borderRadius: "6px" }}
          />
        ))}
      </Stack>
    </StyledTableCell>
  );
};

export const TLCell = ({
  children,
  name,
  sub,
  avatar,
  avatarSrc,
  align,
  after,
  ...props
}) => {
  return (
    <StyledTableCell {...props}>
      <Box display="flex" flexDirection="row" flexWrap="nowrap">
        {name && (
          <Box display="flex" alignItems={"center"}>
            {avatar && (
              <Avatar
                variant="circular"
                sx={{ width: 44, height: 44, bgcolor: "transparent", mr: 2 }}
                {...(avatarSrc
                  ? { src: avatarSrc }
                  : {
                      children: (
                        <Icon name={"Person"} sx={{ color: gray200 }} />
                      ),
                    })}
              />
            )}
            {!sub ? (
              name
            ) : (
              <Box display={"flex"} flexDirection={"column"}>
                <Box>{name}</Box>
                <P>{sub}</P>
              </Box>
            )}
          </Box>
        )}
        {children && <Box>{children}</Box>}
      </Box>
      {after}
    </StyledTableCell>
  );
};

const TableCellAvatar = ({ src }) => {
  return (
    <Avatar
      variant="circular"
      sx={{ width: 44, height: 44, bgcolor: "transparent", mr: 2 }}
      {...(src
        ? { src: src }
        : {
            children: <Icon name={"Person"} sx={{ color: gray200 }} />,
          })}
    />
  );
};

export const User = ({ avatarSrc, email, name }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flex: 1,
      }}
    >
      {avatarSrc && <TableCellAvatar src={avatarSrc} />}
      {!email ? (
        name
      ) : (
        <Box
          sx={{
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <P emphasized>{name}</P>
          <P>{email}</P>
        </Box>
      )}
    </Box>
  );
};

export const UserCell = ({
  firstName,
  lastName,
  name = formatName({ firstName, lastName }),
  email,
  avatarSrc,
  component = "th",
  scope = "row",
  onClick,
  ...props
}) => {
  const hasData = Boolean(name || email || avatarSrc);
  const UserInfoComponent = onClick ? ButtonBase : Box;

  return (
    <StyledTableCell
      onClick={onClick}
      variant={onClick ? "actionable" : undefined}
      component={UserInfoComponent}
      disableRipple
      {...props}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          alignItems: "center",
        }}
      >
        {!hasData ? null : <User {...{ avatarSrc, email, name }} />}
        {onClick && <Icon name="ChevronRight" />}
      </Box>
    </StyledTableCell>
  );
};

const parseAndFormat = ({ utcStr, i18n, defaultStr = "" }) => {
  try {
    // if (!utcStr) return "";
    // return utcStr;
    // const formattedDate = new Date(utcStr).toLocaleDateString();
    const parsed = i18n.parseUTCLocal(utcStr);
    const formattedDate = i18n.formatLocal(parsed, "Pp");
    // console.log("[parseAndFormat]", { parsed, formattedDate });
    return formattedDate;
  } catch (e) {
    console.error("[parseAndFormat]", { e, utcStr, i18n, defaultStr });
    return defaultStr;
  }
};

export const DateCell = ({ utcStr, defaultStr = utcStr || "", ...props }) => {
  const { i18n } = useContext(I18nContext);
  const formattedDate = parseAndFormat({ utcStr, i18n, defaultStr });
  // const formattedDate = utcStr;
  // console.log("DateCellInner", { utcStr, formattedDate, defaultStr });

  return (
    <StyledTableCell {...props} title={utcStr}>
      <Box display="flex" flexDirection="row" flexWrap="nowrap">
        {formattedDate}
      </Box>
    </StyledTableCell>
  );
};

// TODO: extract to components
export const TLLoadableTable = ({
  query,
  getRowsFromQuery = prop("data"),
  columns = [],
  key = "username",
  expandedRowRender,
  bodyBefore = null,
  headerBefore = null,
  selectedKeys,
}) => {
  const isRowSelected = useCallback(
    (row) => selectedKeys?.includes?.(row[key]),
    [key, selectedKeys],
  );

  return (
    <TableContainer component={Box}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        {headerBefore && <TableHead>{headerBefore}</TableHead>}
        {columns && (
          <TableHead>
            <TableRow>
              {columns.map(({ label, key }) => (
                <TLCell key={key || label}>{label}</TLCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {bodyBefore}
          {query && (
            <QueryRenderer
              loaderEl={
                <StyledTableRow>
                  {/* <StyledTableCell colSpan={columns.length}>
                    <Skeleton />
                  </StyledTableCell> */}
                  {columns.map((_, i) => (
                    <StyledTableCell key={i}>
                      <Skeleton />
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              }
              query={query}
              success={(query) =>
                getRowsFromQuery(query).map((row) => {
                  const expandedEl = expandedRowRender?.({ row, columns });
                  // console.log(">>>", { data, expandedEl, row, columns });

                  if (!expandedEl)
                    return (
                      <StyledTableRow
                        key={row[key]}
                        selected={isRowSelected(row)}
                        //   sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        {columns.map((column) => (
                          <Fragment key={column.key || column.label}>
                            {column.render(row)}
                          </Fragment>
                        ))}
                      </StyledTableRow>
                    );

                  return (
                    <Fragment key={row[key]}>
                      <StyledTableRow
                        selected={isRowSelected(row)}
                        //   sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        {columns.map((column) => (
                          <Fragment key={column.key || column.label}>
                            {column.render(row)}
                          </Fragment>
                        ))}
                      </StyledTableRow>

                      {expandedEl}
                    </Fragment>
                  );
                })
              }
            />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
